import { modals } from '@mantine/modals'
import showModalNotification from '@v2/commons/modals/ModalNotification/showModalNotification'
import { handleMutationError } from '@v2/commons/utils/errorHandling/handleMutationError'
import { useRouter } from '@v2/commons/utils/navigation'
import {
  useAddDoctorLocationMutation,
  useUpdateClinicMembershipLocationMutation,
} from '@v2/domains/clinics/api/clinic.api'
import { IUpdateClinicMembershipLocationDto } from '@v2/domains/clinics/clinics.type'
import { DEFAULT_ROUTES } from '@v2/domains/routing/types/routing.types'
import { getRoutePath } from '@v2/domains/routing/utils/route.utils'

import { AddDoctorClinicLocationDto } from 'src/api/generated'

import {
  useDeleteUserMembershipMutation,
  useGenerateApiKeyMutation,
  useUpdateUserMembershipMutation,
} from '../api/memberhisp.api'
import { IUserMembership } from '../membership.types'

export const useMembershipMutations = () => {
  const router = useRouter()

  const [updateMembershipMutation, updateMembershipStatus] =
    useUpdateUserMembershipMutation()
  const [deleteMembershipMutation, deleteMembershipStatus] =
    useDeleteUserMembershipMutation()
  const [generateApiKeyMutation, generateApiKeyStatus] =
    useGenerateApiKeyMutation()
  const [updateMembershipLocationMutation, updateMembershipLocationStatus] =
    useUpdateClinicMembershipLocationMutation()
  const [addDoctorLocationMutation, addDoctorLocationStatus] =
    useAddDoctorLocationMutation()

  const updateMembership = async (
    membershipData: Pick<IUserMembership, 'id' | 'role' | 'status'>,
  ): Promise<boolean> => {
    try {
      await updateMembershipMutation({ ...membershipData }).unwrap()
      modals.closeAll()
      showModalNotification({
        title: 'Success',
        subtitle: 'Membership successfully updated!',
      })
      return true
    } catch (err) {
      return handleMutationError(err, 'Updating user')
    }
  }

  const deleteMembership = async (membershipId: string): Promise<boolean> => {
    try {
      if (!membershipId) {
        const msg = 'User ID is required for update'
        return handleMutationError(null, msg)
      }

      await deleteMembershipMutation(membershipId).unwrap()
      modals.closeAll()
      showModalNotification({
        title: 'Success',
        subtitle: 'Membership removed successfully!',
      })
      router.push(`${getRoutePath(DEFAULT_ROUTES.ADMINISTRATION_USERS)}`)
      return true
    } catch (err) {
      return handleMutationError(err, 'Error updating user')
    }
  }

  const deleteProviderMembership = async (
    membershipId: string,
  ): Promise<boolean> => {
    try {
      await deleteMembershipMutation(membershipId).unwrap()
      modals.closeAll()
      showModalNotification({
        title: 'Success',
        subtitle: 'Provider removed successfully!',
      })
      return true
    } catch (err) {
      return handleMutationError(err, 'Error updating user')
    }
  }

  const generateApiKey = async (userId: string): Promise<void> => {
    try {
      await generateApiKeyMutation(userId).unwrap()
      modals.closeAll()
      showModalNotification({
        title: 'Success',
        subtitle:
          'API key set to database. Contact the engineer to get more info.',
      })
      return
    } catch (err) {
      handleMutationError(err, 'Error Updating user')
    }
  }

  const updateMembershipLocation = async (
    membershipLocation: IUpdateClinicMembershipLocationDto,
  ): Promise<boolean> => {
    try {
      await updateMembershipLocationMutation({ ...membershipLocation }).unwrap()
      modals.closeAll()
      showModalNotification({
        title: 'Success',
        subtitle: 'Provider Location successfully updated!',
      })
      return true
    } catch (err) {
      return handleMutationError(err, 'Provider Location user')
    }
  }

  const addDoctorLocation = async (
    doctorLocation: AddDoctorClinicLocationDto,
  ): Promise<boolean> => {
    try {
      await addDoctorLocationMutation({ ...doctorLocation }).unwrap()
      modals.closeAll()
      showModalNotification({
        title: 'Success',
        subtitle: 'Provider successfully updated!',
      })
      return true
    } catch (err) {
      return handleMutationError(err, 'Add Provider')
    }
  }

  return {
    updateMembership,
    isLoadingUpdate: updateMembershipStatus.isLoading,
    deleteMembership,
    isLoadingDeleteMembership: deleteMembershipStatus.isLoading,
    generateApiKey,
    isLoadingGenerateApi: generateApiKeyStatus.isLoading,
    deleteProviderMembership,
    isLoadingDeleteProviderMembership: deleteMembershipStatus.isLoading,
    updateMembershipLocation,
    isLoadingMembershipLocation: updateMembershipLocationStatus.isLoading,
    addDoctorLocation,
    isLoadingAddDoctorLocation: addDoctorLocationStatus.isLoading,
  }
}
